/**
 * API Credentials Tab Component
 * Displays partner API keys and credentials
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { regeneratePartnerKeys } from '../../lib/api';
import { toast } from 'sonner';

const APICredentialsTab = ({ partner, onKeysRegenerated, sdkDocs }) => {
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleRegenerateKeys = async () => {
    if (!window.confirm('This will invalidate your current API keys. Continue?')) return;
    
    try {
      const res = await regeneratePartnerKeys();
      onKeysRegenerated?.(res.data.api_key, res.data.secret_key);
      toast.success('API keys regenerated');
    } catch (error) {
      toast.error('Failed to regenerate keys');
    }
  };

  return (
    <div className="space-y-6 mt-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">API Credentials</CardTitle>
          <CardDescription>Use these credentials to integrate ParachPay into your platform</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Partner ID</Label>
            <div className="flex gap-2">
              <Input value={partner.partner_id} readOnly className="font-mono" />
              <Button variant="outline" size="icon" onClick={() => copyToClipboard(partner.partner_id, 'Partner ID')}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>API Key</Label>
            <div className="flex gap-2">
              <Input type={showApiKey ? 'text' : 'password'} value={partner.api_key} readOnly className="font-mono" />
              <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="icon" onClick={() => copyToClipboard(partner.api_key, 'API Key')}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Secret Key (for webhook verification)</Label>
            <div className="flex gap-2">
              <Input type={showSecretKey ? 'text' : 'password'} value={partner.secret_key} readOnly className="font-mono" />
              <Button variant="outline" size="icon" onClick={() => setShowSecretKey(!showSecretKey)}>
                {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="icon" onClick={() => copyToClipboard(partner.secret_key, 'Secret Key')}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <Button variant="outline" onClick={handleRegenerateKeys} className="rounded-xl">
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate Keys
          </Button>
        </CardContent>
      </Card>

      {sdkDocs && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Quick Start</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded-xl overflow-x-auto text-sm">
              <code>{sdkDocs.javascript_sdk?.usage}</code>
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default APICredentialsTab;
